/**
 * CanvasReplay — replays a drawing from canvasJSON + strokeTimeline.
 *
 * Renders Fabric.js path objects as SVG paths, progressively revealing them
 * based on the strokeTimeline. Supports play/pause, speed control, and scrubbing.
 *
 * When strokeTimeline is absent, falls back to revealing strokes one at a time
 * with a fixed interval (so old canvasJSON without timing still works).
 *
 * Shared player — no data fetching. The admin console wraps this in a modal that
 * fetches stroke data; the student/teacher attempt-detail page wraps it in
 * `CanvasReplayLoader` (fetches the public canvas-JSON blob). See
 * docs/FeatureDesigns/canvas-json-blob-storage.md.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './CanvasReplay.css';

import {
  parsePaths,
  computeViewBox,
  buildReplaySteps,
  fabricPathToSvgD,
} from './canvasReplaySteps';

// Re-export so existing `import { StrokeTimelineEvent } from './CanvasReplay'`
// keeps working now that the type lives in the pure module.
export type { StrokeTimelineEvent } from './canvasReplaySteps';
import type { StrokeTimelineEvent } from './canvasReplaySteps';

interface CanvasReplayProps {
  canvasJSON: string;
  strokeTimeline?: StrokeTimelineEvent[] | null;
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
}

// ── Component ──────────────────────────────────────────────────────────

export function CanvasReplay({ canvasJSON, strokeTimeline }: CanvasReplayProps) {
  const allPaths = useMemo(() => parsePaths(canvasJSON), [canvasJSON]);
  const viewBox = useMemo(() => computeViewBox(allPaths), [allPaths]);

  // Build the replay steps from the best available timing source:
  // 1. strokeTimeline array (full event log with undo/redo/clear)
  // 2. timestamp fields on path objects (per-stroke timing embedded in canvasJSON)
  // 3. Synthetic 0.5s intervals (no timing data at all)
  const { steps, timingSource } = useMemo(
    () => buildReplaySteps(strokeTimeline, allPaths),
    [strokeTimeline, allPaths],
  );

  const totalDuration = steps.length > 0 ? steps[steps.length - 1].elapsed : 0;

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const animFrameRef = useRef<number>(0);
  const lastTickRef = useRef<number>(0);

  // Default view = the completed drawing (final solution). Park currentTime at
  // the end whenever the data (totalDuration) loads/changes. Pressing play
  // restarts from the beginning (handlePlayPause resets to 0 when at the end).
  useEffect(() => {
    setCurrentTime(totalDuration);
  }, [totalDuration]);

  // Count of visible paths at currentTime
  const visibleCount = useMemo(() => {
    let count = 0;
    for (const step of steps) {
      if (step.elapsed <= currentTime) count = step.pathIndex + 1;
      else break;
    }
    return Math.min(count, allPaths.length);
  }, [currentTime, steps, allPaths.length]);

  // Current step info for display
  const currentStepInfo = useMemo(() => {
    let last: typeof steps[0] | null = null;
    for (const step of steps) {
      if (step.elapsed <= currentTime) last = step;
      else break;
    }
    return last;
  }, [currentTime, steps]);

  // Animation loop
  const tick = useCallback(() => {
    const now = performance.now();
    const dt = (now - lastTickRef.current) / 1000 * speed;
    lastTickRef.current = now;

    setCurrentTime(prev => {
      const next = prev + dt;
      if (next >= totalDuration) {
        setPlaying(false);
        return totalDuration;
      }
      return next;
    });

    animFrameRef.current = requestAnimationFrame(tick);
  }, [speed, totalDuration]);

  useEffect(() => {
    if (playing) {
      lastTickRef.current = performance.now();
      animFrameRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [playing, tick]);

  const handlePlayPause = () => {
    if (currentTime >= totalDuration) {
      setCurrentTime(0);
    }
    setPlaying(p => !p);
  };

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    setCurrentTime(t);
    setPlaying(false);
  };

  const handleSpeedChange = () => {
    const speeds = [0.5, 1, 2, 4, 8];
    const idx = speeds.indexOf(speed);
    setSpeed(speeds[(idx + 1) % speeds.length]);
  };

  // Find the next pause/gap for display
  const nextGap = useMemo(() => {
    for (let i = 1; i < steps.length; i++) {
      if (steps[i].elapsed > currentTime && steps[i].elapsed - steps[i - 1].elapsed > 3) {
        return { at: steps[i].elapsed, duration: steps[i].elapsed - steps[i - 1].elapsed };
      }
    }
    return null;
  }, [currentTime, steps]);

  if (allPaths.length === 0) {
    return <div className="canvas-replay-empty">No drawing data to replay</div>;
  }

  return (
    <div className="canvas-replay">
      {/* SVG canvas */}
      <div className="canvas-replay-viewport">
        <svg
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
          className="canvas-replay-svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <rect x={viewBox.x} y={viewBox.y} width={viewBox.w} height={viewBox.h} fill="white" />
          {allPaths.slice(0, visibleCount).map((p, i) => (
            <path
              key={i}
              d={fabricPathToSvgD(p.path)}
              stroke={p.isEraser ? '#ffffff' : (p.stroke || '#000000')}
              strokeWidth={p.strokeWidth || 2}
              fill="none"
              strokeLinecap={(p.strokeLineCap as any) || 'round'}
              strokeLinejoin={(p.strokeLineJoin as any) || 'round'}
              opacity={p.opacity ?? 1}
            />
          ))}
          {/* Highlight the most recently drawn stroke */}
          {visibleCount > 0 && playing && (() => {
            const latest = allPaths[visibleCount - 1];
            return latest ? (
              <rect
                x={latest.left - 3}
                y={latest.top - 3}
                width={latest.width + 6}
                height={latest.height + 6}
                fill="none"
                stroke="rgba(59, 130, 246, 0.4)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                rx={3}
              />
            ) : null;
          })()}
        </svg>
      </div>

      {/* Controls */}
      <div className="canvas-replay-controls">
        <button className="replay-btn play-btn" onClick={handlePlayPause} title={playing ? 'Pause' : 'Play'}>
          {playing ? '⏸' : currentTime >= totalDuration ? '⏮' : '▶'}
        </button>

        <span className="replay-time">{formatElapsed(currentTime)}</span>

        <input
          type="range"
          className="replay-scrubber"
          min={0}
          max={totalDuration}
          step={0.05}
          value={currentTime}
          onChange={handleScrub}
        />

        <span className="replay-time">{formatElapsed(totalDuration)}</span>

        <button className="replay-btn speed-btn" onClick={handleSpeedChange} title="Playback speed">
          {speed}x
        </button>

        <span className="replay-stroke-count">
          {visibleCount}/{allPaths.length} strokes
        </span>
      </div>

      {/* Info bar */}
      <div className="canvas-replay-info">
        {currentStepInfo && nextGap && (
          <span className="replay-gap-hint">
            Next pause: {nextGap.duration.toFixed(0)}s gap at {formatElapsed(nextGap.at)}
          </span>
        )}
        {timingSource === 'path-timestamps' && (
          <span className="replay-path-timestamps">Timing from path timestamps (no undo/redo tracking)</span>
        )}
        {timingSource === 'synthetic' && (
          <span className="replay-no-timeline">No timing data — using synthetic playback</span>
        )}
      </div>
    </div>
  );
}
